import { ComponentProps } from 'react';

import { GetServerSideProps } from 'next';

import { getStaticPageProps, StaticPageProps } from 'ssr/getStaticPageProps';
import { getServerSideProps as getCmsServerSideProps } from 'ssr/getServerSideProps';
import { HubRouteProps } from 'hub/HubRoute.types';
import { HubRoute } from 'hub/HubRoute';
import { HUB_BASE, resolveHubRoute } from 'hub/server/hub.server';

import { PageWithErrorHandling } from 'Page';

import { defaultLocale, locales } from 'libs/locale';

import { findStaticPageKey, localizedStaticSlug, staticPages } from 'landing-pages/registry';
import { StaticPageRoute } from 'landing-pages/StaticPageRoute';

type CmsProps = ComponentProps<typeof PageWithErrorHandling>;

type Props =
  | { isStaticPage: true; isHubPage?: false; slug: string; staticProps: StaticPageProps }
  | { isStaticPage?: false; isHubPage: true; lang: string; hub: HubRouteProps }
  | ({ isStaticPage?: false; isHubPage?: false } & CmsProps);

const RouteComponent = (props: Props) => {
  if (props.isHubPage) {
    return <HubRoute {...props.hub} lang={props.lang} />;
  }

  return props.isStaticPage ? (
    <StaticPageRoute slug={props.slug} {...props.staticProps} />
  ) : (
    <PageWithErrorHandling {...props} />
  );
};

export const getServerSideProps: GetServerSideProps<Props> = async (ctx) => {
  const routeParts = Array.isArray(ctx.params?.route) ? (ctx.params?.route as string[]) : [];

  // Knowledge hub (src/hub) - resolved from content.json, before the CMS flow.
  // Strip an optional leading locale, then everything under `/knowledge`.
  {
    const parts = [...routeParts];
    const hubLang =
      parts[0] && parts[0].length === 2 && (locales as readonly string[]).includes(parts[0])
        ? (parts.shift() as string)
        : defaultLocale;

    if (parts[0] === HUB_BASE) {
      const hub = resolveHubRoute(hubLang, parts.slice(1));
      if (hub) {
        return { props: { isHubPage: true, lang: hubLang, hub } };
      }
      return { notFound: true };
    }
  }

  let [lang = '', category] = routeParts;

  if ((!lang || lang.length > 2) && !category) {
    category = lang;
    lang = defaultLocale;
  }

  const resolvedLang = lang || defaultLocale;

  // English key requested under a language that has a localized slug → 301 to it,
  // rather than 404, so any stray link to the un-localized path still lands.
  if (category && resolvedLang !== defaultLocale) {
    const localized = localizedStaticSlug(category, resolvedLang);
    if (localized !== category) {
      return { redirect: { destination: `/${resolvedLang}/${localized}`, permanent: true } };
    }
  }

  const staticKey = category ? findStaticPageKey(resolvedLang, category) : undefined;

  if (staticKey) {
    // Canonicalize: if this language has a localized slug, redirect any other
    // accepted form (e.g. the English key under /it) to the localized URL.
    const canonicalSlug = localizedStaticSlug(staticKey, resolvedLang);
    if (category !== canonicalSlug) {
      return {
        redirect: {
          destination: `${resolvedLang !== defaultLocale ? `/${resolvedLang}` : ''}/${canonicalSlug}`,
          permanent: true,
        },
      };
    }

    return {
      props: {
        isStaticPage: true,
        slug: staticKey,
        staticProps: await getStaticPageProps(resolvedLang, staticPages[staticKey].internationalizationKey, staticKey),
      },
    };
  }

  // ssr/getServerSideProps.tsx builds its props via removeFalsy(), which
  // types as `T | undefined` — its real runtime shape always matches
  // GetServerSidePropsResult<Props> for this (non-static) branch.
  return getCmsServerSideProps(ctx) as ReturnType<GetServerSideProps<Props>>;
};

// eslint-disable-next-line import/no-default-export
export default RouteComponent;
